import type { LucideIcon } from "lucide-react";
import { Folders, Star, FileText, Globe, Layers, LayoutGrid } from "lucide-react";
import {
  FREE_TIER_MAX_FOLDERS,
  FREE_TIER_MAX_PUBLISHED,
  FREE_TIER_MAX_SCROLLS_PER_FOLDER,
  FREE_TIER_MAX_STARRED_NOTES,
  FREE_TIER_MAX_WORKSPACES,
  MAX_SECTIONS_PER_NOTE,
  PRO_TIER_MAX_FOLDERS,
  PRO_TIER_MAX_PUBLISHED,
  PRO_TIER_MAX_SCROLLS_PER_FOLDER,
  PRO_TIER_MAX_WORKSPACES,
  type PlanLimitErrorCode,
} from "@/lib/plan-limits";

export type ProUpgradeLimitVariant =
  | "workspaces"
  | "folders"
  | "starred"
  | "notesPerFolder"
  | "publish"
  | "sections";

export type ProUpgradeLimitContent = {
  title: string;
  description: string;
  icon: LucideIcon;
  iconClassName?: string;
  showUpgradeCta?: boolean;
};

export const PRO_UPGRADE_LIMIT_COPY: Record<
  ProUpgradeLimitVariant,
  ProUpgradeLimitContent
> = {
  workspaces: {
    title: "Ready for more?",
    description: `Free includes ${FREE_TIER_MAX_WORKSPACES} workspace. Pro gives you ${PRO_TIER_MAX_WORKSPACES} workspaces and more room to build.`,
    icon: LayoutGrid,
    showUpgradeCta: true,
  },
  folders: {
    title: "Ready for more?",
    description: `You have reached the Free limit of ${FREE_TIER_MAX_FOLDERS} folders in this workspace. Pro adds more folders, notes per folder, and publishing.`,
    icon: Folders,
    showUpgradeCta: true,
  },
  starred: {
    title: "Ready for more?",
    description: `Free includes ${FREE_TIER_MAX_STARRED_NOTES} starred notes. Pro includes unlimited starred notes. Your library stays fully accessible either way.`,
    icon: Star,
    iconClassName: "fill-amber-400/90 text-amber-500",
    showUpgradeCta: true,
  },
  notesPerFolder: {
    title: "Ready for more?",
    description: `You have reached the Free limit of ${FREE_TIER_MAX_SCROLLS_PER_FOLDER} notes in this folder. Pro adds more room in each folder and across your workspace.`,
    icon: FileText,
    showUpgradeCta: true,
  },
  publish: {
    title: "Ready for more?",
    description: `You have reached the Free limit of ${FREE_TIER_MAX_PUBLISHED} live published notebooks. Pro lets you publish more and share more of your work.`,
    icon: Globe,
    showUpgradeCta: true,
  },
  sections: {
    title: "Ready for more?",
    description: `Each note supports ${MAX_SECTIONS_PER_NOTE} sections on your current plan. Pro adds more folders, notes per folder, and publishes.`,
    icon: Layers,
    showUpgradeCta: true,
  },
};

/** Copy when a paying user hits the Pro-tier cap (no Free upgrade pitch). */
export const PRO_AT_LIMIT_COPY: Record<
  ProUpgradeLimitVariant,
  ProUpgradeLimitContent
> = {
  workspaces: {
    title: "Workspace limit reached",
    description: `You're on Pro and have reached the maximum of ${PRO_TIER_MAX_WORKSPACES} workspaces for this plan.`,
    icon: LayoutGrid,
    showUpgradeCta: false,
  },
  folders: {
    title: "Folder limit reached",
    description: `You're on Pro and have reached the maximum of ${PRO_TIER_MAX_FOLDERS} folders in this workspace. Archived folders still count toward this limit.`,
    icon: Folders,
    showUpgradeCta: false,
  },
  starred: {
    title: "Starred note limit reached",
    description:
      "You've reached the starred note limit for your account on this plan.",
    icon: Star,
    iconClassName: "fill-amber-400/90 text-amber-500",
    showUpgradeCta: false,
  },
  notesPerFolder: {
    title: "Note limit reached",
    description: `You're on Pro and have reached the maximum of ${PRO_TIER_MAX_SCROLLS_PER_FOLDER} notes in this folder.`,
    icon: FileText,
    showUpgradeCta: false,
  },
  publish: {
    title: "Publish limit reached",
    description: `You're on Pro and have reached the maximum of ${PRO_TIER_MAX_PUBLISHED} live published notebooks.`,
    icon: Globe,
    showUpgradeCta: false,
  },
  sections: {
    title: "Section limit reached",
    description: `Each note supports up to ${MAX_SECTIONS_PER_NOTE} sections on Pro. Remove or consolidate sections to add more.`,
    icon: Layers,
    showUpgradeCta: false,
  },
};

export function resolveProUpgradeLimitContent(
  variant: ProUpgradeLimitVariant,
  isPro: boolean,
): ProUpgradeLimitContent {
  return isPro ? PRO_AT_LIMIT_COPY[variant] : PRO_UPGRADE_LIMIT_COPY[variant];
}

/** Map API limit codes to upgrade dialog variants (call-site may override). */
export function proUpgradeVariantFromErrorCode(
  code: string | undefined,
): ProUpgradeLimitVariant | null {
  switch (code as PlanLimitErrorCode | undefined) {
    case "FREE_WORKSPACE_LIMIT":
      return "workspaces";
    case "FREE_FOLDER_LIMIT":
      return "folders";
    case "FREE_STAR_LIMIT":
      return "starred";
    case "FREE_PUBLISH_LIMIT":
      return "publish";
    case "FREE_NOTE_LIMIT":
      return "sections";
    case "FREE_NOTEBOOK_LIMIT":
      return "notesPerFolder";
    default:
      return null;
  }
}
